import { NamedElementSelector } from './types';
import { NamedElementRecord } from '@fullstory/proto/fs/services/search-settings';
import { parseSelector, makeOrderedSelectorRules } from '@fullstory/selectors';
import { calculate, compare } from 'specificity';
import { Trie } from '@fullstory/util/trie';

// Parses the named elements into a list of selectors that can be provided to the NamedElementMatcher
export const namedElementsToSelectors = (nes: NamedElementRecord[]): NamedElementSelector[] => {
  const selectors: NamedElementSelector[] = [];
  nes.forEach(ne => {
    if (!ne.namedElement?.platformSelectors) return;
    ne.namedElement.platformSelectors.forEach(platformSelector => {
      try {
        const parsedSelector = parseSelector(platformSelector.selector);
        const orderedSelectorRules = makeOrderedSelectorRules(parsedSelector);
        selectors.push({
          id: ne.namedElement!.id || ne.namedElement!.suggestion?.id || '',
          name: ne.namedElement!.name,
          platforms: platformSelector.platforms,
          selector: platformSelector.selector,
          parsedSelector,
          orderedSelectorRules,
        });
      } catch (e) {
        console.error(`Error parsing ${platformSelector.selector}:`, e);
      }
    });
  });
  return selectors;
};

// Sorts named element electors in most specific to least specific order.
export const sortSelectorsBySpecificity = (
  selectors: NamedElementSelector[],
): NamedElementSelector[] =>
  selectors
    .map(selector => ({
      selector,
      // The specificity library does not handle commas correctly. It parses them to split multiple selectors for
      // comparison even if the comma appears in an attribute value. We are not doing any multi selector calculation.
      // Removing the comma will not change the specificity.
      specificity: calculate(selector.selector.replace(/,/g, '')),
    }))
    .sort((a, b) => {
      try {
        return -compare(
          a.specificity[0]?.specificityArray ?? '',
          b.specificity[0]?.specificityArray ?? '',
        );
      } catch (e) {
        // Shouldn't happen at this point now that we are using specificity arrays.
        // Log, ignore, and return a default value to avoid blowing up the app.
        console.error(`CSS specificity compare failed: ${JSON.stringify(e)}`);
        return 0;
      }
    })
    .map(s => s.selector);

// since the trie implementation is really focused on words, translate
// common css selectors into words to preserve searching
const translationMap: { [key: string]: string } = {
  '#': 'POUNDPOUNDPOUND',
  '.': 'CLASSCLASSCLASS',
  '[': 'OPENBRACKETOPENBRACKET',
  ']': 'CLOSEDBRACKETCLOSEDBRACKET',
  '=': 'EQUALEQUALS',
  '"': 'OPENQUOTEOPENQUOTE',
  '>': ' ', // descendant operators we can ignore, swapping it for a space
  '-': 'DASHDASH',
  '~': 'TILDETILDE',
  '+': 'PLUSPLUS',
  ':': 'COLONCOLON',
  '*': 'STARSTAR',
  '|': 'BARBAR',
  '^': 'CARETCARET',
};
// helper function to convert every instance of the items in the translation map into words, to prevent splitting on these values
const convert = (str: string, map: { [key: string]: string }) => {
  let outstring = str;
  for (const key in map) {
    outstring = outstring.split(key).join(map[key]);
  }
  return outstring;
};
/**
 * This helper is designed to take the input CSS selector, and then translate it into individual
 * matches, to allow partial matching, for the main case of handling elements
 *
 * eg:
 * 'div[data-testid="hi"]' => 'div div[data-testid="hi"] [data-testid="hi"]'
 *
 * The trie splits on spaces, so this allows that selector to match searches for any one of those three options.
 */
export const parseInputString = (str: string): string => {
  // first split the string across all main boundaries (classes, ids, attributes)
  const values = str
    .replace(/#/g, ' #')
    .replace(/\./g, ' .')
    .replace(/>/g, ' ') // descendant attributes, we can almost fully ignore
    .replace(/\[/g, ' [')
    .split(' ');

  const includingElements = [];
  for (let i = 0; i < values.length; i++) {
    includingElements.push(values[i]);
    if (i == 0) continue;
    const previousElem = values[i - 1] ?? '';
    // we consider any previous
    if (previousElem[0]?.match(/\w/) != null) {
      includingElements.push(previousElem + values[i]);
    }
  }
  return includingElements.join(' ');
};

export const createCSSTrie = <T>(): Pick<Trie<T>, 'put' | 'get' | 'clear'> => {
  const trie = new Trie<T>();
  return {
    put: (str: string, value: T) => {
      trie.put(convert(parseInputString(str), translationMap), value);
      trie.put(convert(str, translationMap), value);
    },
    get: (str: string) => trie.get(convert(str, translationMap)),
    clear: () => {
      trie.clear();
    },
  };
};
